// Shared service-worker registration helper.
//
// Why this is a separate module and not inlined in main.tsx:
//
// The parent-app is consumed in two ways. In production it's both
// (a) a federation remote that the shell mounts inside its own page,
// and (b) a standalone PWA at parent.<your-domain> that parents
// install from "Add to Home Screen". The SW only matters for the
// standalone path (push subscriptions are pinned to the parent-app
// origin), but a future change might want to call this from inside
// the shell too. Keeping it pure-TS + idempotent makes both safe.
//
// The module-scoped `registered` guard means the function is safe to
// call multiple times — registration goes through once, subsequent
// calls return the same Promise.

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

/**
 * Register the parent-app's service worker. Safe to call multiple
 * times — only the first call talks to the browser. Returns null
 * (and never throws) when:
 *
 *   - Not running in a browser (jsdom / SSR / unit test).
 *   - The build is not production (`import.meta.env.PROD === false`).
 *   - `serviceWorker` isn't available on `navigator` (very old
 *     browsers, content-blocked tabs).
 *   - The browser refuses the registration (logged via console.warn;
 *     surfaced as null so the caller can decide).
 */
export function registerParentAppSW(): Promise<ServiceWorkerRegistration | null> {
  if (registration) return registration;

  registration = (async () => {
    if (typeof window === "undefined") return null;
    if (!import.meta.env.PROD) return null;
    if (!("serviceWorker" in navigator)) return null;
    try {
      return await navigator.serviceWorker.register("/sw.js");
    } catch (err) {
      // SW registration failure is non-fatal — the app still works,
      // it just can't receive push notifications until the next page
      // load succeeds.
      console.warn("parent-app: SW registration failed", err);
      return null;
    }
  })();

  return registration;
}
